/**
 * The collapsible sidebar's state, as plain functions.
 *
 * This lives apart from the component on purpose. `@croissants/ui` ships no DOM
 * test environment - rendering is covered by the browser end-to-end suites
 * (tech-stack section 12) - so the parts that can actually be got wrong are
 * pulled out here where a node test can reach them: what is written to storage,
 * what is read back from a corrupted value, and which ARIA attributes a control
 * carries in each state.
 */

/**
 * The four portals sit on four subdomains and therefore four localStorage areas,
 * so one key is enough; `AppShell` still takes an override for a portal that ever
 * needs two independent shells.
 */
export const SIDEBAR_STORAGE_KEY = 'cib.sidebar.collapsed';

/**
 * The slice of `Storage` this needs. Narrowed to two methods so a test can pass a
 * plain object and so nothing here can reach for the rest of the Web Storage API.
 */
export interface SidebarStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Reads the persisted collapse state.
 *
 * Expanded is the default and every failure resolves to it: an absent key, a
 * value written by an older build, or a `getItem` that throws - Safari in private
 * browsing throws on Web Storage rather than returning null. A sidebar that
 * cannot read its preference must still be a usable sidebar, and expanded is the
 * state in which every label is legible.
 */
export function readSidebarCollapsed(
  storage: SidebarStorage | null | undefined,
  key: string = SIDEBAR_STORAGE_KEY,
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * Persists the collapse state. Silent on failure for the same reason: a quota
 * error or a blocked storage area is not worth breaking the click over - the
 * panel still collapses, it just forgets by the next visit.
 */
export function writeSidebarCollapsed(
  storage: SidebarStorage | null | undefined,
  collapsed: boolean,
  key: string = SIDEBAR_STORAGE_KEY,
): void {
  if (!storage) return;
  try {
    storage.setItem(key, collapsed ? 'true' : 'false');
  } catch {
    /* Preference lost, panel unaffected. */
  }
}

export interface SidebarToggleAria {
  /** The sidebar is "expanded" when it is NOT collapsed. */
  readonly 'aria-expanded': boolean;
  /** The id of the element this control expands and collapses. */
  readonly 'aria-controls': string;
}

/**
 * The ARIA a collapse control must carry.
 *
 * `aria-expanded` is deliberately the negation of `collapsed`: the attribute
 * describes the region, the flag describes the control's effect, and writing
 * `aria-expanded={collapsed}` is the bug this function exists to make impossible.
 */
export function sidebarToggleAria(collapsed: boolean, controlsId: string): SidebarToggleAria {
  return { 'aria-expanded': !collapsed, 'aria-controls': controlsId };
}

/**
 * The ARIA the mobile drawer's hamburger carries. Same shape, opposite polarity -
 * a drawer is expanded when it is open.
 */
export function drawerToggleAria(open: boolean, controlsId: string): SidebarToggleAria {
  return { 'aria-expanded': open, 'aria-controls': controlsId };
}
